import Icon from "src/shared/icons"
import { useEffect, useState } from "react"
import styles from "./EvidenceUploadItem.module.css"
import { createPortal } from "react-dom"
import { ReactNode } from "react"

type ModalImageProps = {
    isOpen: boolean
    children: ReactNode | ReactNode[]
}

type EvidenceUploadProps = {
    onFilesChange?: (files: (File | null)[]) => void
}

const ModalImage = ({ children, isOpen = true }: ModalImageProps) => {
    const portal = createPortal(
        <div className={styles["evidence-upload-item__modal"]}>{children}</div>,
        document.getElementById("snackbar") as HTMLElement
    )
    return isOpen ? portal : null
}

function EvidenceUpload({ onFilesChange }: EvidenceUploadProps) {
    const [files, setFiles] = useState<(File | null)[]>([null, null, null])
    const [previews, setPreviews] = useState<(string | null)[]>([null, null, null])
    const [selectedPreview, setSelectedPreview] = useState<string | null>(null)
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

    useEffect(() => {
        const newPreviews = files.map((file) => (file ? URL.createObjectURL(file) : null))
        setPreviews(newPreviews)

        return () => {
            newPreviews.forEach((url) => {
                if (url) URL.revokeObjectURL(url)
            })
        }
    }, [files])

    const handleFileChange = (index: number, file: File) => {
        const updatedFiles = [...files]
        updatedFiles[index] = file
        setFiles(updatedFiles)
        onFilesChange?.(updatedFiles)
    }

    const handleDelete = () => {
        if (selectedIndex === null) return
        const updatedFiles = [...files]
        const updatedPreviews = [...previews]
        updatedFiles[selectedIndex] = null
        updatedPreviews[selectedIndex] = null
        setFiles(updatedFiles)
        setPreviews(updatedPreviews)
        setSelectedPreview(null)
        setSelectedIndex(null)
        onFilesChange?.(updatedFiles)
    }

    return (
        <div className={styles["evidence-upload-item__container"]}>
            {[0, 1, 2].map((i) => (
                <div key={i} className={styles["evidence-upload-item__box"]}>
                    {previews[i] ? (
                        <img
                            src={previews[i] ?? ""}
                            alt={`preview-${i}`}
                            className={styles["evidence-upload-item__preview"]}
                            onClick={() => {
                                setSelectedPreview(previews[i])
                                setSelectedIndex(i)
                                document.activeElement instanceof HTMLElement && document.activeElement.blur()
                            }}
                        />
                    ) : (
                        <>
                            <input
                                type="file"
                                id={`upload-input-${i}`}
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                        handleFileChange(i, e.target.files[0])
                                    }
                                }}
                            />
                            <label htmlFor={`upload-input-${i}`} style={{ cursor: "pointer" }}>
                                <Icon
                                    name="CameraPlus"
                                    height={24}
                                    width={24}
                                    color="#6941C6"
                                    className={styles["evidence-upload-item__icon"]}
                                />
                            </label>
                        </>
                    )}
                </div>
            ))}

            {selectedPreview && (
                <ModalImage isOpen={true}>
                    <div className={styles["evidence-upload-item__modal-card"]}>
                        <div className={styles["evidence-upload-item__modal-header"]}>
                            <Icon
                                name="close"
                                height={20}
                                width={20}
                                color="#101828"
                                className={styles["evidence-upload-item__close"]}
                                onClick={() => {
                                    setSelectedPreview(null)
                                    setSelectedIndex(null)
                                    document.activeElement instanceof HTMLElement && document.activeElement.blur()
                                }}
                            />
                        </div>

                        <div className={styles["evidence-upload-item__modal-content"]}>
                            <img
                                src={selectedPreview}
                                alt="Preview"
                                className={styles["evidence-upload-item__modal-img"]}
                            />
                        </div>

                        <button className={styles["evidence-upload-item__delete"]} onClick={handleDelete}>
                            Borrar foto
                        </button>
                    </div>
                </ModalImage>
            )}
        </div>
    )
}

export default EvidenceUpload
